const getBase = () => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { pathname, hostname } = window.location;
    if (hostname === 'zendesk.speedy-taxi.com') return '/api';
    if (pathname.startsWith('/zendesk')) return '/zendesk/api';
  }
  return '/api';
};
const API_BASE = getBase();

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`.replace(/\/+/g, '/');
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  });
  const contentType = res.headers.get('content-type') || '';
  const text = await res.text();
  if (!contentType.includes('application/json')) {
    if (!res.ok) throw new Error(text.slice(0, 100) || res.statusText);
    throw new Error('Server returned HTML instead of JSON. Check API URL.');
  }
  const data = JSON.parse(text);
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export const api = {
  login: (password: string) =>
    fetchApi<{ success: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  getTickets: () => fetchApi<{ tickets: Ticket[]; byFilter: Record<string, Ticket[]>; filterIds: string[] }>('/tickets/list'),
  getRecap: () => fetchApi<{ recap: string; suggestions: Suggestion[]; ticketCount: number }>('/tickets/recap'),
  getTicket: (id: string, withRecap = false) =>
    fetchApi<{ ticket: Ticket; comments: Comment[]; users: User[]; recap?: string }>(
      `/tickets/ticket/${id}${withRecap ? '?recap=1' : ''}`
    ),
  getTicketRecap: (id: string) => fetchApi<{ recap: string }>(`/tickets/ticket/${id}/recap`),
  generateReply: (id: string, note?: string) =>
    fetchApi<{ reply: string }>(`/tickets/ticket/${id}/generate-reply`, {
      method: 'POST',
      body: JSON.stringify({ note: note || '' }),
    }),
  sendReply: (id: string, body: string, isPublic = true) =>
    fetchApi<{ success: boolean; comment: unknown }>(`/tickets/ticket/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ body, public: isPublic }),
    }),
  getAuditLogs: (action = 'login') =>
    fetchApi<{ audit_logs: AuditLog[] }>(`/audit-logs?action=${encodeURIComponent(action)}`),
  getStatus: () => fetchApi<{
    working: boolean;
    tunisiaTime: string;
    nextLogins: { date: string; start: string; end: string }[];
    cronState: { active: boolean; schedule: string; description: string; lastCheck: string; inWorkingHours: boolean };
  }>('/status'),
};

export interface Ticket {
  id: number;
  subject: string;
  description?: string;
  status: string;
  priority?: string;
  created_at: string;
  updated_at: string;
  requester_id?: number;
  assignee_id?: number;
  filterId?: string;
}

export interface Comment {
  id: number;
  body: string;
  public: boolean;
  author_id: number;
  created_at: string;
}

export interface User {
  id: number;
  name: string;
  email?: string;
}

export interface Suggestion {
  ticketId: number;
  summary: string;
  suggestion: string;
}

export interface AuditLog {
  id: number;
  action: string;
  actor_id: number;
  actor_name: string;
  created_at: string;
  ip_address?: string;
  change_description?: string;
  source_type?: string;
  source_label?: string;
}
